/**
 * Catalog API
 *
 * Provider-agnostic activity/experience catalog. All provider-specific logic
 * lives on the backend (Viator, GetYourGuide, Google Places, etc.).
 *
 * Uses /experiences/v1/* (client-facing, JWT auth). The backend's /catalog/v1/*
 * endpoints are admin-only and not invoked here.
 */

import type { AxiosInstance } from 'axios';
import type {
  AvailabilityParams,
  AvailabilityResponse,
  BookingInitResponse,
  BookingRequest,
  BookingResponse,
  CatalogCategory,
  CatalogItem,
  CatalogItemSummary,
  CatalogSearchParams,
  CatalogSearchResponse,
  RecommendationsParams,
  RecommendationsResponse,
} from '@wg/types';

const EXPERIENCES_BASE = '/experiences/v1';

export interface InitiateBookingOptions {
  date?: string;
  participants?: number;
  theme?: 'light' | 'dark' | 'auto';
  /** Stored attribution campaign (RN install match / web first touch).
   * Stamped on the tracking session; the user's first-touch claim wins
   * over it server-side. */
  campaign_code?: string;
}

export interface CatalogApi {
  // Search & Discovery
  searchCatalog(params: CatalogSearchParams): Promise<CatalogSearchResponse>;
  getCatalogItem(itemId: string): Promise<CatalogItem>;
  getCategories(destination?: string): Promise<CatalogCategory[]>;
  getRecommendations(
    params: RecommendationsParams,
  ): Promise<RecommendationsResponse[]>;
  getTrendingItems(
    destination?: string,
    limit?: number,
  ): Promise<CatalogItemSummary[]>;

  // Availability & Booking
  getAvailability(
    itemId: string,
    params: AvailabilityParams,
  ): Promise<AvailabilityResponse>;
  getReviews(
    itemId: string,
    params?: { page?: number; limit?: number },
  ): Promise<{ reviews: any[]; total: number; average_rating: number }>;
  preBook(
    itemId: string,
    request: Partial<BookingRequest>,
  ): Promise<{ valid: boolean; errors?: string[]; quote?: any }>;
  createBooking(
    itemId: string,
    request: BookingRequest,
  ): Promise<BookingResponse>;
  initiateBooking(
    itemId: string,
    options?: InitiateBookingOptions,
  ): Promise<BookingInitResponse>;
  reportLikelyBooked(
    trackingSessionId: string,
    detectedUrl: string,
    confirmationCode?: string,
  ): Promise<{ success: boolean; message: string }>;
  /** @deprecated requires admin auth — use initiateBooking() */
  getBookingUrl(
    itemId: string,
    options?: {
      slot_id?: string;
      travelers?: number;
      trip_id?: string;
      return_url?: string;
    },
  ): Promise<{ url: string; type: 'redirect' | 'iframe'; external?: boolean }>;

  // Favorites
  saveItem(itemId: string): Promise<{ saved: boolean }>;
  unsaveItem(itemId: string): Promise<{ saved: boolean }>;
  getSavedItems(
    page?: number,
    limit?: number,
  ): Promise<{ items: CatalogItemSummary[]; total: number }>;
}

export function createCatalogApi(http: AxiosInstance): CatalogApi {
  return {
    async searchCatalog(params) {
      const response = await http.get<CatalogSearchResponse>(
        `${EXPERIENCES_BASE}/search`,
        {
          params,
        },
      );
      return response.data;
    },

    async getCatalogItem(itemId) {
      const response = await http.get<CatalogItem>(
        `${EXPERIENCES_BASE}/${itemId}`,
      );
      return response.data;
    },

    async getCategories(destination) {
      const response = await http.get<{ categories: CatalogCategory[] }>(
        `${EXPERIENCES_BASE}/categories`,
        { params: { destination } },
      );
      return response.data.categories;
    },

    async getRecommendations(params) {
      const response = await http.get<{ sections: RecommendationsResponse[] }>(
        `${EXPERIENCES_BASE}/recommended`,
        { params },
      );
      return response.data.sections;
    },

    async getTrendingItems(destination, limit = 10) {
      const response = await http.get<{ items: CatalogItemSummary[] }>(
        `${EXPERIENCES_BASE}/trending`,
        { params: { destination, limit } },
      );
      return response.data.items;
    },

    async getAvailability(itemId, params) {
      const response = await http.get<AvailabilityResponse>(
        `${EXPERIENCES_BASE}/${itemId}/availability`,
        { params },
      );
      return response.data;
    },

    async getReviews(itemId, params) {
      const response = await http.get<{
        reviews: any[];
        total: number;
        average_rating: number;
      }>(`${EXPERIENCES_BASE}/${itemId}/reviews`, { params });
      return response.data;
    },

    async preBook(itemId, request) {
      const response = await http.post<{
        valid: boolean;
        errors?: string[];
        quote?: any;
      }>(`${EXPERIENCES_BASE}/${itemId}/pre-book`, request);
      return response.data;
    },

    async createBooking(itemId, request) {
      const response = await http.post<BookingResponse>(
        `${EXPERIENCES_BASE}/${itemId}/booking`,
        request,
      );
      return response.data;
    },

    async initiateBooking(itemId, options) {
      const response = await http.post<BookingInitResponse>(
        `${EXPERIENCES_BASE}/${itemId}/book`,
        {
          experience_id: itemId,
          date: options?.date,
          participants: options?.participants ?? 1,
          theme: options?.theme ?? 'auto',
          ...(options?.campaign_code
            ? { campaign_code: options.campaign_code }
            : {}),
        },
      );
      return response.data;
    },

    async reportLikelyBooked(trackingSessionId, detectedUrl, confirmationCode) {
      try {
        const response = await http.post<{ session: any; message: string }>(
          '/bookings/likely-booked',
          {
            tracking_session_id: trackingSessionId,
            detected_url: detectedUrl,
            possible_confirmation_code: confirmationCode,
          },
        );
        return { success: true, message: response.data.message };
      } catch (error: any) {
        // Non-fatal — don't block the UI on detection-report failures
        return { success: false, message: error.message || 'Failed to report' };
      }
    },

    async getBookingUrl(itemId, options) {
      const response = await http.post<{
        url: string;
        type: 'redirect' | 'iframe';
        external?: boolean;
      }>('/catalog/v1/booking-url', { item_id: itemId, ...options });
      return response.data;
    },

    async saveItem(itemId) {
      const response = await http.post<{ saved: boolean }>(
        `${EXPERIENCES_BASE}/saved/${itemId}`,
      );
      return response.data;
    },

    async unsaveItem(itemId) {
      const response = await http.delete<{ saved: boolean }>(
        `${EXPERIENCES_BASE}/saved/${itemId}`,
      );
      return response.data;
    },

    async getSavedItems(page = 1, limit = 20) {
      const response = await http.get<{
        items: CatalogItemSummary[];
        total: number;
      }>(`${EXPERIENCES_BASE}/saved`, { params: { page, limit } });
      return response.data;
    },
  };
}
